import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileUp, FileCheck2, Loader2, Download, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const GOLD_INK = '#8F650F';
const TERRA = '#E07A5F';

const STATUS_LABEL = {
  eingereicht: { text: 'Eingereicht — wird geprüft', color: GOLD_INK },
  angenommen: { text: 'Angenommen', color: '#2F7A3D' },
  ueberarbeiten: { text: 'Bitte überarbeiten', color: TERRA },
};

// Upload-Karte für die Facharbeit (Praxisanleiter / PDL) — mobiloptimiert
export const FacharbeitUploadCard = ({ courseId, getAuthHeader }) => {
  const [submission, setSubmission] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/facharbeit/my/${courseId}`, getAuthHeader());
      setSubmission(r.data.submission);
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Datei zu groß (max. 25 MB)'); return; }
    const fd = new FormData();
    fd.append('course_id', courseId);
    fd.append('file', file);
    setUploading(true);
    setProgress(0);
    try {
      await axios.post(`${API}/facharbeit/upload`, fd, {
        ...getAuthHeader(),
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded / (ev.total || 1)) * 100)),
      });
      toast.success('Facharbeit hochgeladen');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const st = submission ? (STATUS_LABEL[submission.status] || STATUS_LABEL.eingereicht) : null;

  return (
    <div className="bg-cream-100 border border-cream-300 rounded-2xl p-5 sm:p-6" data-testid="facharbeit-card">
      <p className="font-mono text-[9.5px] uppercase font-semibold mb-1.5" style={{ letterSpacing: '0.22em', color: GOLD_INK }}>
        Facharbeit
      </p>
      <h3 className="font-display text-[19px] text-espresso leading-snug mb-1">
        Facharbeit einreichen
      </h3>
      <p className="text-[13px] text-sand-600 leading-relaxed mb-4">
        Lade deine fertige Facharbeit als PDF oder Word-Datei hoch (max. 25 MB).
        Du kannst jederzeit eine neue Version hochladen.
      </p>

      {submission && (
        <div className="mb-4 p-3.5 bg-cream-50 border border-cream-300 rounded-xl" data-testid="facharbeit-status">
          <div className="flex items-start gap-2.5">
            {submission.status === 'ueberarbeiten'
              ? <AlertCircle className="w-[18px] h-[18px] flex-shrink-0 mt-0.5" style={{ color: st.color }} />
              : <FileCheck2 className="w-[18px] h-[18px] flex-shrink-0 mt-0.5" style={{ color: st.color }} />}
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold" style={{ color: st.color }}>{st.text}</p>
              <p className="text-[12px] text-sand-600 truncate mt-0.5">
                {submission.filename} · Version {submission.version} · {new Date(submission.uploaded_at).toLocaleDateString('de-DE')}
              </p>
              {submission.feedback && (
                <p className="text-[12.5px] text-sand-700 mt-2 italic leading-relaxed" data-testid="facharbeit-feedback">
                  Feedback: {submission.feedback}
                </p>
              )}
            </div>
          </div>
          <a
            href="#dl"
            onClick={async (ev) => {
              ev.preventDefault();
              try {
                const r = await axios.get(`${API}/facharbeit/my/${courseId}/download`, { ...getAuthHeader(), responseType: 'blob' });
                const url = URL.createObjectURL(r.data);
                const a = document.createElement('a');
                a.href = url; a.download = submission.filename; a.click();
                URL.revokeObjectURL(url);
              } catch { toast.error('Download fehlgeschlagen'); }
            }}
            className="inline-flex items-center gap-1.5 text-[12px] text-sand-500 hover:text-terra mt-2.5"
            data-testid="facharbeit-my-download"
          >
            <Download className="w-3.5 h-3.5" /> Meine Datei herunterladen
          </a>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={onFile}
        className="hidden"
        id={`facharbeit-input-${courseId}`}
        data-testid="facharbeit-file-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-full text-[12px] font-bold uppercase transition-colors disabled:opacity-60"
        style={{ backgroundColor: TERRA, color: '#FBF7F0', letterSpacing: '0.14em' }}
        data-testid="facharbeit-upload-btn"
      >
        {uploading
          ? (<><Loader2 className="w-4 h-4 animate-spin" /> Lädt hoch… {progress}%</>)
          : (<><FileUp className="w-4 h-4" /> {submission ? 'Neue Version hochladen' : 'Facharbeit hochladen'}</>)}
      </button>
    </div>
  );
};

export default FacharbeitUploadCard;
